import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, CustomSelect, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton, RowActions, RowAction,
  ListEmptyRow, StatusDot, LoadingButton, LoadingSpinner, btnPrimary, btnSecondary,
} from '@redpanda/ui';
import type { StatusTone } from '@redpanda/ui';
import { createKeyedCache, formatTableDate, toIntlLocale, useRefetchOnFocus, useRememberedState } from '@redpanda/utils';
import { bankReconciliationService } from '../services/api';
import { useBankAccountOptionsStore } from '../stores/bankAccountStore';
import { notifyDataChanged, useDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';

interface ImportRecord {
  id: string;
  fileName: string;
  importDate: string;
  totalRecords: number;
  successRecords: number;
  failedRecords: number;
  status: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface MatchingStatistics {
  totalBankTransactions: number;
  totalStatementTransactions: number;
  unmatchedBankTransactions: number;
  unmatchedStatementTransactions: number;
}

const importTone: Record<string, StatusTone> = { COMPLETED: 'success', PARTIAL: 'warning', FAILED: 'danger', PROCESSING: 'accent' };

// 依帳戶快取（R40）：換帳戶、回到這頁先顯示上次的資料、背景更新
const useImports = createKeyedCache('accounting-reconciliation-imports', async (q: { accountId: string }) =>
  (await bankReconciliationService.getImportRecords(q.accountId, 1, 50)).data.data as ImportRecord[]);
const useStatistics = createKeyedCache('accounting-reconciliation-stats', async (q: { accountId: string }) =>
  (await bankReconciliationService.getStatistics(q.accountId)).data as MatchingStatistics);

/**
 * 對帳管理：選帳戶 → 匯入銀行對帳單 → 自動比對（信心度 80 以上直接對帳）
 * 原本 2 張大圖示卡片＋藍色說明框；匯入記錄因為讀錯回應格式永遠顯示「沒有記錄」、「查看詳情」按鈕沒有作用
 */
const BankReconciliationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const accounts = useBankAccountOptionsStore(state => state.accounts);
  const fetchAccounts = useBankAccountOptionsStore(state => state.fetchAll);
  const [accountId, setAccountId] = useRememberedState<string>('accounting-ui:reconciliation.account', '');
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ImportRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // 記住的帳戶已經不存在（被刪除）或還沒選過時，預設第一個使用中的帳戶
  useEffect(() => {
    if (accounts.length === 0) return;
    if (!accountId || !accounts.some(a => a.id === accountId)) {
      const first = accounts.find(a => a.isActive) ?? accounts[0];
      setAccountId(first.id);
    }
  }, [accounts, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = accountId ? { accountId } : null;
  const imports = useImports(query);
  const statistics = useStatistics(query);
  const reload = () => {
    imports.refetch();
    statistics.refetch();
  };
  useEffect(reload, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefetchOnFocus(reload);
  useDataChanged('bankTransactions', statistics.refetch);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !accountId) return;
    setUploading(true);
    try {
      const { data } = await bankReconciliationService.importStatement(accountId, file);
      toast.success(t('bankReconciliation.toast.importSuccess', { count: data.successRecords ?? 0 }));
      reload();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'bankReconciliation.toast.importFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleAutoMatch = async () => {
    if (!accountId) return;
    setMatching(true);
    try {
      const { data } = await bankReconciliationService.autoMatch(accountId);
      toast.success(data.autoApplied > 0
        ? t('bankReconciliation.toast.matchSuccess', { count: data.autoApplied })
        : t('bankReconciliation.toast.noMatch'));
      if (data.manualReview > 0) toast.success(t('bankReconciliation.toast.manualReview', { count: data.manualReview }));
      reload();
      notifyDataChanged('bankTransactions');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'bankReconciliation.toast.matchFailed'));
    } finally {
      setMatching(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await bankReconciliationService.deleteImportRecord(deleteTarget.id);
      toast.success(t('common.messages.deleteSuccess'));
      reload();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.deleteFailed'));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const locale = toIntlLocale(i18n.language);
  const stats = statistics.data;
  const canMatch = !!accountId && !!stats && stats.unmatchedStatementTransactions > 0 && stats.unmatchedBankTransactions > 0;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('bankReconciliation.pageTitle')}</h1>
          <div className="flex space-x-3 whitespace-nowrap">
            <input ref={fileInput} type="file" accept=".csv,.xls,.xlsx" className="sr-only" onChange={handleFile} />
            <button
              type="button"
              disabled={!accountId || uploading}
              onClick={() => fileInput.current?.click()}
              className={`${btnSecondary} gap-2 disabled:opacity-50 disabled:pointer-events-none`}
            >
              {uploading && <LoadingSpinner size="sm" variant="secondary" />}
              {uploading ? t('common.status.uploading') : t('bankReconciliation.importStatement')}
            </button>
            <LoadingButton type="button" loading={matching} disabled={!canMatch} onClick={handleAutoMatch} className={btnPrimary}>
              {t('bankReconciliation.startMatching')}
            </LoadingButton>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="md:w-96">
          <CustomSelect
            value={accountId}
            onChange={setAccountId}
            placeholder={t('bankReconciliation.selectAccount')}
            searchable={accounts.length > 8}
            options={accounts.map(account => ({ value: account.id, label: `${account.accountName}（${account.bankName} ${account.accountNumber}）` }))}
          />
        </div>
        {accountId && stats && (
          <p className="text-sm text-fg-secondary tabular-nums md:ml-auto">
            {t('bankReconciliation.summary.bank', { unmatched: stats.unmatchedBankTransactions, total: stats.totalBankTransactions })}
            <span className="mx-2 text-fg-muted">·</span>
            {t('bankReconciliation.summary.statement', { unmatched: stats.unmatchedStatementTransactions, total: stats.totalStatementTransactions })}
            <span className="mx-2 text-fg-muted">·</span>
            <Link to={`/bank-accounts/${accountId}`} className="text-accent hover:underline">{t('bankReconciliation.viewTransactions')}</Link>
          </p>
        )}
      </div>

      {!accountId ? (
        <div className="bg-surface rounded-lg border border-line px-5 py-12 text-center">
          <p className="text-sm font-medium text-fg">{accounts.length === 0 ? t('bankReconciliation.noAccounts') : t('bankReconciliation.selectAccount')}</p>
          <p className="mt-1 text-sm text-fg-secondary">
            {accounts.length === 0 ? t('bankReconciliation.noAccountsHint') : t('bankReconciliation.selectAccountToStart')}
          </p>
          {accounts.length === 0 && <Link to="/bank-accounts" className={`${btnPrimary} mt-4`}>{t('bankAccounts.list.addButton')}</Link>}
        </div>
      ) : !imports.data ? (
        <TableSkeleton rows={5} columns={6} showHeader={true} />
      ) : (
        <section>
          <h2 className="text-base font-semibold text-fg mb-3">{t('bankReconciliation.importRecords')}</h2>
          <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
            <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <Table style={{ tableLayout: 'fixed', minWidth: '820px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead numeric className="w-40">{t('bankReconciliation.table.importTime')}</TableHead>
                    <TableHead>{t('bankReconciliation.table.fileName')}</TableHead>
                    <TableHead numeric className="w-48">{t('bankReconciliation.table.period')}</TableHead>
                    <TableHead numeric className="w-32">{t('bankReconciliation.table.records')}</TableHead>
                    <TableHead className="w-28">{t('bankReconciliation.table.status')}</TableHead>
                    <TableHead className="w-20">{t('bankReconciliation.table.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {imports.data.length === 0 ? (
                    <ListEmptyRow colSpan={6} filtered={false} title={t('bankReconciliation.noRecords')} hint={t('bankReconciliation.noRecordsHint')} />
                  ) : (
                    imports.data.map(record => (
                      <TableRow key={record.id}>
                        <TableCell numeric>
                          {new Date(record.importDate).toLocaleString(locale, { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell className="truncate font-medium text-fg" title={record.fileName}>{record.fileName}</TableCell>
                        <TableCell numeric>
                          {record.startDate && record.endDate
                            ? `${formatTableDate(record.startDate, i18n.language)} – ${formatTableDate(record.endDate, i18n.language)}`
                            : <span className="text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell numeric>
                          {record.successRecords}
                          {record.failedRecords > 0 && (
                            <span className="block text-[13px] text-red-600 dark:text-red-400">{t('bankReconciliation.failedCount', { count: record.failedRecords })}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusDot tone={importTone[record.status] ?? 'neutral'} label={t(`bankReconciliation.status.${record.status}`)} />
                        </TableCell>
                        <TableCell>
                          <RowActions>
                            <RowAction tone="danger" onClick={() => setDeleteTarget(record)}>{t('common.actions.delete')}</RowAction>
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          <p className="mt-3 text-[13px] text-fg-secondary">{t('bankReconciliation.formatHint')}</p>
        </section>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('bankReconciliation.confirmDeleteTitle')}
        message={t('bankReconciliation.confirmDeleteMessage', { name: deleteTarget?.fileName ?? '' })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={deleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default BankReconciliationPage;
